import React from 'react';
import { Box, Button, ChakraProvider, Heading, Stack, Text } from '@chakra-ui/react';
import { createTheme } from '@common/ui';

import { useSystemTranslation } from 'hooks';
import { PaymentAttemptBootstrap } from 'services/payment-handoff';

import { readStoredSdkTheme } from './launchContext';

interface Props {
  attempt?: PaymentAttemptBootstrap;
  busy: boolean;
  restoreFailed?: boolean;
  onRefresh: () => void;
  onContinue: () => void;
  onAbandon: () => void;
}

/** Recovery never guesses whether an upstream payment succeeded. */
export function PaymentRecoveryView({
  attempt,
  busy,
  restoreFailed,
  onRefresh,
  onContinue,
  onAbandon,
}: Props) {
  const { tSystem } = useSystemTranslation();
  const canAbandon =
    !restoreFailed &&
    attempt &&
    ['awaiting_handoff', 'ready', 'handoff_pending', 'handoff_ready'].includes(attempt.status);
  const canContinue = !restoreFailed && attempt?.status === 'checkout_ready';
  let messageKey = 'payment.international.recoveryPending';
  if (canAbandon) messageKey = 'payment.international.restartAvailable';
  if (canContinue) messageKey = 'payment.international.continueExisting';
  if (restoreFailed) messageKey = 'payment.international.handoffFailed';
  let colors = null;
  try {
    colors = JSON.parse(attempt?.launchContext.brandingColors || 'null');
  } catch {
    /* Use the standard viewer theme if saved branding is malformed. */
  }
  const theme = createTheme(colors, readStoredSdkTheme(true));
  return (
    <ChakraProvider theme={theme}>
      <Box minH="100vh" bg="gray.50" px={6} py={{ base: 12, md: 24 }}>
        <Box
          maxW="lg"
          mx="auto"
          bg="white"
          borderWidth="1px"
          borderRadius="lg"
          p={8}
          boxShadow="sm"
          role="alert"
        >
          <Stack spacing={5}>
            <Heading size="md">{tSystem('payment.international.recoveryHeading')}</Heading>
            <Text>{tSystem(messageKey)}</Text>
            {canContinue && (
              <Button isLoading={busy} onClick={onContinue}>
                {tSystem('payment.international.continuePayment')}
              </Button>
            )}
            {canAbandon && (
              <Button isLoading={busy} onClick={onAbandon}>
                {tSystem('payment.international.returnToApplication')}
              </Button>
            )}
            {!canAbandon && !canContinue && (
              <Button isLoading={busy} onClick={onRefresh}>
                {tSystem(
                  restoreFailed ? 'common.retry' : 'payment.international.checkPaymentStatus',
                )}
              </Button>
            )}
            {attempt && (
              <Text fontSize="sm" color="gray.600">
                {tSystem('payment.international.paymentReference', {
                  reference: attempt.attemptId,
                })}
              </Text>
            )}
          </Stack>
        </Box>
      </Box>
    </ChakraProvider>
  );
}
